import type { AgentStep, AgentTask, AgentTaskType } from '@septima/contract';
import { useState } from 'react';
import { Link } from 'react-router';
import { Check, ChevronRight, CheckCircle2, XCircle, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import EmailDraftButton from '@/components/email/EmailDraftButton';
import { useProviderDraft } from '@/components/email/use-provider-draft';
import { useAgentPanel } from './AgentPanel';
import { useRunDetail } from './RunDetailPanel';
import { triggerDownload } from '@/lib/download';

import { useArtefactSummary } from '@/lib/use-review-queue';

export const TASK_TYPE_LABELS: Record<AgentTaskType, string> = {
    post_call_processing: 'Post-call processing',
    call_classification_batch: 'Call classification (batch)',
    post_call_processing_batch: 'Post-call processing (batch)',
    fact_find_prefill_batch: 'Fact-find prefill (batch)',
    suitability_check: 'Suitability check',
    monthly_report: 'Monthly report',
    monthly_narrative: 'Monthly narrative',
    kyc_renewal: 'KYC renewal',
    consumer_duty_rescore: 'Consumer Duty rescore',
    breach_draft: 'Breach draft',
    sanctions_screening: 'Sanctions screening',
    reconciliation_classification_batch: 'Reconciliation classification',
    suitability_letter_draft: 'Suitability letter',
    onboarding_pack_draft: 'Onboarding pack',
    email_draft: 'Email draft',
    onboarding_pipeline_watch: 'Onboarding pipeline watch',
    vulnerability_flag: 'Vulnerability flag',
    framework_deviation: 'Framework deviation',
    fund_policy_watch: 'Fund policy watch',
    data_freshness_check: 'Data freshness check',
    fund_rns_draft: 'Fund RNS draft',
    vulnerability_adaptation_plan: 'Vulnerability adaptation plan',
    governance_paper_draft: 'Governance paper draft',
    fair_value_assessment: 'Fair value assessment',
    workflow_run: 'Workflow run',
};

const TASK_TYPE_ROUTES: Record<AgentTaskType, string> = {
    post_call_processing: '/desk/calls-meetings',
    // Classification gates the downstream passes; its row lives on the calls
    // surface where the callKind badge it sets is shown.
    call_classification_batch: '/desk/calls-meetings',
    // Batched knowledge fan-out lands on the same Client Knowledge surface as
    // the artefacts it produces; prefill batches land on the fact-find register.
    post_call_processing_batch: '/desk/client-knowledge',
    fact_find_prefill_batch: '/desk/fact-find',
    suitability_check: '/comply/suitability',
    monthly_report: '/ops/reporting',
    // MonthlyNarrativePanel renders on /desk/client-knowledge — the
    // routeForTask helper threads ?client=<id> from the task payload.
    monthly_narrative: '/desk/client-knowledge',
    kyc_renewal: '/comply/fin-crime-kyc',
    breach_draft: '/comply/breach-register',
    sanctions_screening: '/comply/fin-crime-kyc',
    consumer_duty_rescore: '/comply/suitability',
    reconciliation_classification_batch: '/ops/reconciliation',
    // Desk → Suitability is globally retired (frontends.ts `hidden`); land
    // SCRIVENER suitability-letter reviews on the surviving Comply surface.
    suitability_letter_draft: '/comply/suitability',
    onboarding_pack_draft: '/desk/my-book',
    email_draft: '/desk/calls-meetings',
    onboarding_pipeline_watch: '/ops/onboarding',
    vulnerability_flag: '/comply/vulnerability',
    framework_deviation: '/desk/framework-compliance',
    fund_policy_watch: '/view/fund-management',
    data_freshness_check: '/comply/data-freshness',
    fund_rns_draft: '/view/fund-management',
    vulnerability_adaptation_plan: '/comply/vulnerability',
    // ManComm Programme view is unwired (issue #208) — land governance paper
    // reviews on the View home instead of deep-linking into the hidden page.
    governance_paper_draft: '/view/firm-overview',
    fair_value_assessment: '/comply/fair-value',
    // WS-A — workflow runs surface on Ops; deep-link back to the editor.
    workflow_run: '/ops/workflows',
};

// Compute the per-task review URL — most task types share the static
// route from TASK_TYPE_ROUTES, but post_call_processing also threads
// the call id from the task payload so the calls-meetings page can
// auto-expand the right row instead of dumping the IM at the list view.
export function routeForTask(task: AgentTask): string {
    if (task.type === 'onboarding_pipeline_watch') {
        if (task.frontend === 'desk') return '/desk/onboarding';
        if (task.frontend === 'comply') return '/comply/onboarding';
        return '/ops/onboarding';
    }
    const base = TASK_TYPE_ROUTES[task.type];
    if (task.type === 'post_call_processing') {
        const callId = task.payload?.callId;
        if (typeof callId === 'string' && callId.length > 0) {
            return `${base}?call=${encodeURIComponent(callId)}`;
        }
    }
    if (task.type === 'monthly_narrative') {
        const clientId = task.payload?.clientId ?? task.clientId;
        if (typeof clientId === 'string' && clientId.length > 0) {
            return `${base}?client=${encodeURIComponent(clientId)}`;
        }
    }
    return base;
}

export const AGENT_NAMES: Record<AgentTaskType, string> = {
    post_call_processing: 'ATLAS',
    call_classification_batch: 'ATLAS',
    post_call_processing_batch: 'ATLAS',
    fact_find_prefill_batch: 'ATLAS',
    monthly_report: 'ATLAS',
    monthly_narrative: 'ATLAS',
    suitability_check: 'VIGIL',
    kyc_renewal: 'AEGIS',
    consumer_duty_rescore: 'AEGIS',
    breach_draft: 'AEGIS',
    sanctions_screening: 'AEGIS',
    reconciliation_classification_batch: 'MASON',
    suitability_letter_draft: 'SCRIVENER',
    onboarding_pack_draft: 'HERALD',
    email_draft: 'EMISSARY',
    onboarding_pipeline_watch: 'WARDEN',
    vulnerability_flag: 'SENTINEL',
    framework_deviation: 'QUORUM',
    fund_policy_watch: 'BEACON',
    data_freshness_check: 'PULSE',
    fund_rns_draft: 'CHARTER',
    vulnerability_adaptation_plan: 'HAVEN',
    governance_paper_draft: 'FORUM',
    fair_value_assessment: 'ARBITER',
    workflow_run: 'WORKFLOW',
};

export function formatTimeOfDay(iso: string | undefined): string {
    if (!iso) return '';
    const match = iso.match(/T(\d{2}):(\d{2})/);
    if (!match) return '';
    return `${match[1]}:${match[2]}`;
}

export default function AgentTaskCard({ task }: { task: AgentTask }) {
    if (task.status === 'running' || task.status === 'queued') {
        return <RunningCard task={task} />;
    }
    if (task.status === 'review_required') {
        return <ReviewCard task={task} />;
    }
    return <CompletedCard task={task} />;
}

// Shared card header: agent name (left) · time (right), mono.
function TaskHeader({
    agent,
    time,
    accent = 'tertiary',
}: {
    agent: string;
    time: string;
    accent?: 'accent' | 'amber' | 'tertiary';
}) {
    const accentCls =
        accent === 'accent'
            ? 'text-[rgb(var(--color-accent))]'
            : accent === 'amber'
              ? 'text-[rgb(var(--color-rag-amber))]'
              : 'text-[rgb(var(--color-text-tertiary))]';
    return (
        <div className="flex items-center justify-between">
            <span className={cn('text-annotation font-medium font-mono tabular-nums', accentCls)}>
                {agent}
            </span>
            <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                {time}
            </span>
        </div>
    );
}

// Shared bordered card scaffold. All four card states (in flight / needs
// review / failed / completed) share this base so they read as one family and
// pop off the panel background; status is conveyed by the leading icon and the
// status tag, not by a different card treatment.
function TaskCardShell({ className, children }: { className?: string; children: React.ReactNode }) {
    return (
        <article
            className={cn(
                'rounded-card p-card-padding flex flex-col',
                'border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))]',
                className,
            )}
        >
            {children}
        </article>
    );
}

// -------- Running / queued state --------

function RunningCard({ task }: { task: AgentTask }) {
    const typeLabel = TASK_TYPE_LABELS[task.type];
    const agent = AGENT_NAMES[task.type];
    return (
        <TaskCardShell className="gap-2.5">
            <TaskHeader agent={agent} time={formatTimeOfDay(task.createdAt)} accent="accent" />

            <div className="flex flex-col gap-0.5">
                <div className="text-body-medium text-[rgb(var(--color-text-primary))]">
                    {typeLabel}
                </div>
                {task.clientName && (
                    <div className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                        {task.clientName}
                    </div>
                )}
                <div className="text-annotation text-[rgb(var(--color-text-tertiary))] truncate">
                    {task.triggerDetail}
                </div>
            </div>

            <ol className="flex flex-col gap-1.5 mt-1 pl-0.5 border-l border-[rgb(var(--color-divider)/0.6)]">
                {task.steps.map((step) => (
                    <StepRow key={step.id} step={step} />
                ))}
            </ol>
        </TaskCardShell>
    );
}

function StepRow({ step }: { step: AgentStep }) {
    return (
        <li className="flex items-start gap-2 -ml-[5px] text-annotation">
            <StepIcon status={step.status} />
            <div className="flex-1 min-w-0 pt-px">
                <span
                    className={cn(
                        step.status === 'pending' && 'text-[rgb(var(--color-text-tertiary))]',
                        step.status === 'running' && 'text-[rgb(var(--color-text-primary))]',
                        step.status === 'done' && 'text-[rgb(var(--color-text-secondary))]',
                        step.status === 'error' && 'text-[rgb(var(--color-rag-red))]',
                    )}
                >
                    {step.label}
                    {step.status === 'running' && '…'}
                </span>
                {step.detail && (
                    <div className="text-annotation text-[rgb(var(--color-text-tertiary))] mt-0.5">
                        {step.detail}
                    </div>
                )}
            </div>
        </li>
    );
}

// One shared status marker for the reasoning-trace steps: a done tick, or a
// small dot tinted to the step status (running pulses).
function StepIcon({ status }: { status: AgentStep['status'] }) {
    return (
        <span className="mt-[5px] flex h-2.5 w-2.5 flex-shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-surface))]">
            {status === 'done' ? (
                <Check className="h-2 w-2 text-[rgb(var(--color-rag-green))]" strokeWidth={2.5} />
            ) : (
                <span
                    className={cn(
                        'rounded-full',
                        status === 'pending' ? 'h-1 w-1' : 'h-1.5 w-1.5',
                        status === 'running' && 'bg-[rgb(var(--color-accent))] animate-pulse',
                        status === 'error' && 'bg-[rgb(var(--color-rag-red))]',
                        status === 'pending' && 'bg-[rgb(var(--color-text-tertiary))]',
                    )}
                />
            )}
        </span>
    );
}

// -------- Review required state --------
//
// The drawer is a peek now — approve/reject lives on the Agents Kanban's
// "Needs Review" column (/{frontend}/agents). This card is just a deep link
// into that column, focused on the task it represents.

function ReviewCard({ task }: { task: AgentTask }) {
    const { closePanel } = useAgentPanel();
    const typeLabel = TASK_TYPE_LABELS[task.type];
    const agent = AGENT_NAMES[task.type];
    const when = task.createdAt;
    const focusHref = `${reviewQueueRouteForTask(task)}?focus=${encodeURIComponent(`task:${task.id}`)}`;
    // PRD #65: prefer the agent's emitted summary over the templated
    // `Manual · <client>` triggerDetail. Falls back when no artefact has
    // landed yet (or the task type still flows through a legacy table).
    const artefactSummary = useArtefactSummary(task);

    return (
        <Link
            to={focusHref}
            onClick={closePanel}
            className={cn(
                'group block rounded-card p-card-padding flex flex-col gap-2',
                'border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))]',
                'hover:border-[rgb(var(--color-text-tertiary)/0.5)] hover:shadow-sm',
                'transition-colors duration-card-hover ease-swiss',
            )}
        >
            <TaskHeader agent={agent} time={formatTimeOfDay(when)} accent="amber" />

            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <div className="text-body-medium text-[rgb(var(--color-text-primary))] truncate">
                        {typeLabel}
                    </div>
                    {task.clientName && (
                        <div className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                            {task.clientName}
                        </div>
                    )}
                    <div className="text-annotation text-[rgb(var(--color-text-tertiary))] truncate">
                        {artefactSummary ?? task.triggerDetail}
                    </div>
                </div>
                <ChevronRight
                    className="w-4 h-4 mt-0.5 text-[rgb(var(--color-rag-amber))] flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={1.5}
                />
            </div>
        </Link>
    );
}

// Frontend-scoped Agents Kanban route — desk for IM tasks, ops/comply/view
// for the rest. Mirrors TASK_TYPE_FRONTEND in use-review-queue.ts so the
// drawer link lands on the same persona's board.
function reviewQueueRouteForTask(task: AgentTask): string {
    const frontend = task.frontend === 'global' ? 'desk' : task.frontend;
    return `/${frontend}/agents`;
}

// -------- Download affordance (SCRIVENER / HERALD / ATLAS) --------

function downloadSpecFor(task: AgentTask): { url: string; filename: string; label: string } | null {
    // Keyed on the produced artefact type (PRD #261 follow-up) + the task.id
    // doc-render endpoints. The two composer artefacts with a server-rendered
    // document are the suitability letter (PDF) and the onboarding pack (ZIP).
    const clientName = task.clientName ?? 'Client';
    if (task.artefactType === 'suitability-letter-draft') {
        return {
            url: `/api/docs/suitability-letter/${task.id}`,
            filename: `Suitability Letter - ${clientName}.pdf`,
            label: 'Download letter (PDF)',
        };
    }
    if (task.artefactType === 'onboarding-pack-draft') {
        return {
            url: `/api/docs/onboarding-pack/${task.id}`,
            filename: `Onboarding Pack - ${clientName}.zip`,
            label: 'Download pack (ZIP)',
        };
    }
    return null;
}

export function DownloadRow({ task }: { task: AgentTask }) {
    const spec = downloadSpecFor(task);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    if (!spec) return null;

    const handleClick = async () => {
        if (busy) return;
        setError(null);
        setBusy(true);
        try {
            await triggerDownload(spec.url, spec.filename);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Download failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex flex-col gap-1">
            <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleClick()}
                busy={busy}
                icon={<Download className="w-3 h-3" strokeWidth={2} />}
            >
                {busy ? 'Preparing…' : spec.label}
            </Button>
            {error && <p className="text-annotation text-[rgb(var(--color-rag-red))]">{error}</p>}
        </div>
    );
}

// -------- EMISSARY email draft preview + mailbox push --------

// Draft a generated email into the IM's Outlook mailbox.
// Row/props-based (PRD #261 follow-up): the EMISSARY `client-communication-draft`
// body supplies the recipient / subject / body; the draft call and the
// open-in-mailbox behaviour live in <EmailDraftButton> + useProviderDraft, so
// this surface is uniform with every other "Draft email".
export function EmailDraftRow({
    to,
    subject,
    body,
}: {
    to: string | null;
    subject: string;
    body: string;
}) {
    const { draft, busy } = useProviderDraft();
    return <EmailDraftButton busy={busy} onDraft={() => draft({ to, subject, body })} />;
}

// -------- Completed / approved / rejected state --------

function CompletedCard({ task }: { task: AgentTask }) {
    const { openRun } = useRunDetail();
    const typeLabel = TASK_TYPE_LABELS[task.type];
    const agent = AGENT_NAMES[task.type];
    const isRejected = task.status === 'rejected';
    const isFailed = task.status === 'failed';
    const when = task.reviewedAt ?? task.completedAt ?? task.createdAt;
    const statusLabel = isFailed
        ? 'Failed'
        : isRejected
          ? 'Rejected'
          : task.status === 'approved'
            ? 'Approved'
            : 'Completed';
    const failReason = isFailed ? (task.error?.message ?? task.rejectionReason) : null;

    // The whole card opens the focused run-detail panel — output, glass-box
    // trace, steps, run stats, and (for failures) the recorded reason.
    return (
        <button
            type="button"
            onClick={() => openRun(task.id)}
            className={cn(
                'group w-full text-left rounded-card p-card-padding flex flex-col gap-1.5',
                'border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-primary))]',
                'hover:border-[rgb(var(--color-text-tertiary)/0.5)] hover:shadow-sm',
                'transition-colors duration-card-hover ease-swiss',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent))]',
            )}
        >
            <TaskHeader agent={agent} time={formatTimeOfDay(when)} />

            <div className="flex items-center gap-2">
                {isRejected || isFailed ? (
                    <XCircle
                        className="w-3.5 h-3.5 text-[rgb(var(--color-rag-red))] flex-shrink-0"
                        strokeWidth={1.5}
                    />
                ) : (
                    <CheckCircle2
                        className="w-3.5 h-3.5 text-[rgb(var(--color-rag-green))] flex-shrink-0"
                        strokeWidth={1.5}
                    />
                )}
                <div className="text-body text-[rgb(var(--color-text-secondary))] flex-1 min-w-0 truncate">
                    {typeLabel}
                </div>
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))] flex-shrink-0">
                    {statusLabel}
                </span>
                <ChevronRight
                    className="w-3.5 h-3.5 flex-shrink-0 text-[rgb(var(--color-text-tertiary))] transition-transform group-hover:translate-x-0.5"
                    strokeWidth={1.5}
                />
            </div>

            {task.clientName && (
                <div className="text-annotation text-[rgb(var(--color-text-tertiary))] truncate pl-[22px]">
                    {task.clientName}
                </div>
            )}
            {failReason && (
                <div className="text-annotation text-[rgb(var(--color-rag-red))] pl-[22px] line-clamp-2">
                    {failReason}
                </div>
            )}
        </button>
    );
}
